import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService, { PaginatedResult } from '../../services/AmplifyApiService';
import removeUndefinedQueryParams from '../../shared/helpers/remove-undefined-query-params';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import {
  CertificateFeedbackStatus,
  CertificateStatus,
  ICertificate,
  ICertificatePermission,
  IMyCertificatesTableRow,
  ISupplierCertificatesTableRow,
} from '../../shared/interfaces/ICertificate';
import { ICertificateTask } from '../../shared/interfaces/ICertificateTask';

export type GetMyCertificatesParams = {
  skip?: number;
  size?: number;
  sort?: string;
  type?: string;
  status?: CertificateStatus;
};

export type GetCertificateTasksParams = {
  skip?: number;
  size?: number;
  sort?: string;
  search?: string;
};

export type GetSupplierCertificatesParams = {
  skip?: number;
  size?: number;
  sort?: string;
  reviewStatus?: string;
};

export type GetMyCertificateByIdParams = {
  certificateId: string;
};

export type GetSupplierCertificateByIdParams = {
  certificateId: string;
};

export type GetCertificatePermissionsByIdParams = {
  certificateId: string;
};

export type CreateCertificateProps = {
  type: string;
  registrationNumber: string;
  validFrom: string;
  validUntil?: string;
  appliesToAllSites: boolean;
  enclosedSiteIds: number[];
  file: File;
};

export type UpdateCertificateEnclosedSitesProps = {
  certificateId: string;
  appliesToAllSites: boolean;
  enclosedSiteIds: number[];
};

export type ShareCertificateProps = {
  certificateId: string;
  bpn: string;
};

export type ShareCertificateResponse = {
  message: string;
};

export type CreateCertificatePermissionProps = {
  certificateId: string;
  bpn: string;
  requestId: number | null;
};

export type WithdrawCertificatePermissionProps = {
  permissionId: number;
};

export type CreateCertificatePermissionResponse = {
  message: string;
};

export type SupplierReviewCertificateProps = {
  certificateId: string;
  status: CertificateFeedbackStatus;
};

export type SupplierReviewCertificateResponse = {
  message: string;
};

export type CertificateTypesMap = Record<string, string>;

export type SearchCertificatesParams = {
  certificateType?: string;
  bpn?: string;
};

export interface GetCertificateByBpnItem {
  id: string;
  type: string;
  sites: string[];
  offerId: string;
  isConsumed?: boolean;
}

export interface GetCertificatesByBpnResponse {
  certificates: GetCertificateByBpnItem[];
  url: string;
}

export type PullCertificateProps = {
  bpn: string;
  id: string;
  url: string;
  offerId: string;
};

export type PullCertificateResponse = {
  message: string;
};

export type RequestCertificateProps = {
  type: string;
  bpn: string;
};

export type RequestCertificateResponse = {
  message: string;
};

type ICertificatePdfDownloadUrl = {
  downloadUrl: string;
};

const amplifyApiService = new AmplifyApiService();

const SUPPLIER_CERTIFICATES_BASE_PATH = 'certificate/supplier';
const CERTIFICATE_BASE_PATH = 'certificate';
const CERTIFICATE_TASKS_BASE_PATH = 'certificate/tasks';

export const useGetPaginatedMyCertificates = (
  params: GetMyCertificatesParams = {},
  options: UseQueryOptions<PaginatedResult<IMyCertificatesTableRow[]>, Error> = {},
): UseQueryResult<PaginatedResult<IMyCertificatesTableRow[]>, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: CERTIFICATE_BASE_PATH,
    options: {
      queryParams: removeUndefinedQueryParams(params),
    },
  };

  const query = useQuery<PaginatedResult<IMyCertificatesTableRow[]>, Error>({
    queryKey: [CERTIFICATE_BASE_PATH, params],
    queryFn: async (): Promise<PaginatedResult<IMyCertificatesTableRow[]>> => {
      const response = await amplifyApiService.paginatedGet<IMyCertificatesTableRow[]>(
        amplifyApiConfig,
      );

      return response;
    },
    ...options,
  });

  return query;
};

export const useGetPaginatedCertificateTasks = (
  params: GetCertificateTasksParams = {},
  options: UseQueryOptions<PaginatedResult<ICertificateTask[]>, Error> = {},
): UseQueryResult<PaginatedResult<ICertificateTask[]>, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: CERTIFICATE_TASKS_BASE_PATH,
    options: {
      queryParams: removeUndefinedQueryParams(params),
    },
  };

  const query = useQuery<PaginatedResult<ICertificateTask[]>, Error>({
    queryKey: [CERTIFICATE_TASKS_BASE_PATH, params],
    queryFn: async (): Promise<PaginatedResult<ICertificateTask[]>> => {
      return await amplifyApiService.paginatedGet<ICertificateTask[]>(amplifyApiConfig);
    },
    ...options,
  });

  return query;
};

export const useGetPaginatedSupplierCertificates = (
  params: GetSupplierCertificatesParams = {},
  options: UseQueryOptions<PaginatedResult<ISupplierCertificatesTableRow[]>, Error> = {},
): UseQueryResult<PaginatedResult<ISupplierCertificatesTableRow[]>, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: SUPPLIER_CERTIFICATES_BASE_PATH,
    options: {
      queryParams: removeUndefinedQueryParams(params),
    },
  };

  const query = useQuery<PaginatedResult<ISupplierCertificatesTableRow[]>, Error>({
    queryKey: [SUPPLIER_CERTIFICATES_BASE_PATH, params],
    queryFn: async (): Promise<PaginatedResult<ISupplierCertificatesTableRow[]>> => {
      return await amplifyApiService.paginatedGet<ISupplierCertificatesTableRow[]>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return query;
};

export const useGetCertificateDetailsById = (
  { certificateId }: GetMyCertificateByIdParams,
  options: UseQueryOptions<ICertificate | null, Error> = {},
): UseQueryResult<ICertificate | null, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${CERTIFICATE_BASE_PATH}/${certificateId}`,
  };

  const query = useQuery<ICertificate | null, Error>({
    queryKey: [CERTIFICATE_BASE_PATH, certificateId],
    queryFn: async (): Promise<ICertificate | null> => {
      const response = await amplifyApiService.get<ICertificate>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useGetCertificateTypes = (
  options: UseQueryOptions<CertificateTypesMap, Error> = {},
): UseQueryResult<CertificateTypesMap, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${CERTIFICATE_BASE_PATH}/types`,
    options: {},
  };

  const query = useQuery<CertificateTypesMap, Error>({
    queryKey: [CERTIFICATE_BASE_PATH, 'types'],
    queryFn: async (): Promise<CertificateTypesMap> => {
      const response = await amplifyApiService.get<CertificateTypesMap>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useGetCertificatePermissionsById = (
  { certificateId }: GetCertificatePermissionsByIdParams,
  options: UseQueryOptions<ICertificatePermission[], Error> = {},
): UseQueryResult<ICertificatePermission[], Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${CERTIFICATE_BASE_PATH}/${certificateId}/permissions`,
  };

  const query = useQuery<ICertificatePermission[], Error>({
    queryKey: [CERTIFICATE_BASE_PATH, certificateId, 'permissions'],
    queryFn: async (): Promise<ICertificatePermission[]> => {
      const response = await amplifyApiService.get<ICertificatePermission[]>(amplifyApiConfig);

      return response;
    },
    ...options,
    enabled: options.enabled ?? !!certificateId,
  });

  return query;
};

export const useCreateCertificate = (
  options: UseMutationOptions<ICertificate, Error, CreateCertificateProps> = {},
): UseMutationResult<ICertificate, Error, CreateCertificateProps> => {
  const mutation = useMutation<ICertificate, Error, CreateCertificateProps>({
    mutationFn: async (props: CreateCertificateProps): Promise<ICertificate> => {
      const {
        file,
        type,
        registrationNumber,
        validFrom,
        validUntil,
        appliesToAllSites,
        enclosedSiteIds,
      } = props;

      const formData = new FormData();
      formData.append('type', type);
      formData.append('registrationNumber', registrationNumber);
      formData.append('validFrom', validFrom);
      formData.append('file', file);

      if (validUntil) {
        formData.append('validUntil', validUntil);
      }

      if (appliesToAllSites) {
        formData.append('appliesToAllSites', 'true');
      } else if (enclosedSiteIds.length) {
        formData.append('enclosedSiteIds', enclosedSiteIds.join(','));
      }

      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: CERTIFICATE_BASE_PATH,
        options: {
          body: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      };

      return await amplifyApiService.post<FormData, ICertificate>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useUpdateCertificateEnclosedSites = (
  options: UseMutationOptions<void, Error, UpdateCertificateEnclosedSitesProps> = {},
): UseMutationResult<void, Error, UpdateCertificateEnclosedSitesProps> => {
  const mutation = useMutation<void, Error, UpdateCertificateEnclosedSitesProps>({
    mutationFn: async ({
      certificateId,
      appliesToAllSites,
      enclosedSiteIds,
    }: UpdateCertificateEnclosedSitesProps): Promise<void> => {
      const body: { appliesToAllSites?: boolean; enclosedSiteIds?: number[] } = appliesToAllSites
        ? { appliesToAllSites: true }
        : { enclosedSiteIds };

      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${CERTIFICATE_BASE_PATH}/${certificateId}/enclosed-sites`,
        options: {
          body,
        },
      };

      return await amplifyApiService.put<
        { appliesToAllSites?: boolean; enclosedSiteIds?: number[] },
        void
      >(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useGetCertificatePdfDownloadUrl = (
  id?: number,
): UseQueryResult<ICertificatePdfDownloadUrl> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${CERTIFICATE_BASE_PATH}/${id}/get-certificate-pdf`,
    options: {},
  };

  const query = useQuery<ICertificatePdfDownloadUrl, Error>({
    queryKey: [CERTIFICATE_BASE_PATH, id, 'get-certificate-pdf'],
    queryFn: async (): Promise<ICertificatePdfDownloadUrl> => {
      const response = await amplifyApiService.get<ICertificatePdfDownloadUrl>(amplifyApiConfig);

      return response;
    },
    enabled: false,
  });

  return query;
};

export const useShareCertificate = (
  options: UseMutationOptions<ShareCertificateResponse, Error, ShareCertificateProps> = {},
): UseMutationResult<ShareCertificateResponse, Error, ShareCertificateProps> => {
  const mutation = useMutation<ShareCertificateResponse, Error, ShareCertificateProps>({
    mutationFn: async ({
      certificateId,
      bpn,
    }: ShareCertificateProps): Promise<ShareCertificateResponse> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${CERTIFICATE_BASE_PATH}/${certificateId}/share`,
        options: {
          body: { bpn },
        },
      };

      return await amplifyApiService.post<{ bpn: string }, ShareCertificateResponse>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};

export const useSupplierReviewCertificate = (
  options: UseMutationOptions<
    SupplierReviewCertificateResponse,
    Error,
    SupplierReviewCertificateProps
  > = {},
): UseMutationResult<SupplierReviewCertificateResponse, Error, SupplierReviewCertificateProps> => {
  const mutation = useMutation<
    SupplierReviewCertificateResponse,
    Error,
    SupplierReviewCertificateProps
  >({
    mutationFn: async ({
      certificateId,
      status,
    }: SupplierReviewCertificateProps): Promise<SupplierReviewCertificateResponse> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${CERTIFICATE_BASE_PATH}/${certificateId}/supplier-review`,
        options: {
          body: { status },
        },
      };

      return await amplifyApiService.post<
        { status: CertificateFeedbackStatus },
        SupplierReviewCertificateResponse
      >(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useCreateCertificatePermission = (
  options: UseMutationOptions<
    CreateCertificatePermissionResponse,
    Error,
    CreateCertificatePermissionProps
  > = {},
): UseMutationResult<
  CreateCertificatePermissionResponse,
  Error,
  CreateCertificatePermissionProps
> => {
  const mutation = useMutation<
    CreateCertificatePermissionResponse,
    Error,
    CreateCertificatePermissionProps
  >({
    mutationFn: async ({
      certificateId,
      bpn,
      requestId,
    }: CreateCertificatePermissionProps): Promise<CreateCertificatePermissionResponse> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${CERTIFICATE_BASE_PATH}/${certificateId}/access`,
        options: {
          body: { bpn, requestId },
        },
      };

      return await amplifyApiService.post<
        { bpn: string; requestId: number | null },
        CreateCertificatePermissionResponse
      >(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useSearchCertificates = (
  params: SearchCertificatesParams = {},
  options: UseQueryOptions<GetCertificatesByBpnResponse, Error> = {},
): UseQueryResult<GetCertificatesByBpnResponse, Error> => {
  const { bpn, certificateType } = params;

  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `certificate/get-certificates/${bpn ?? ''}`,
    options: {
      queryParams: removeUndefinedQueryParams({ type: certificateType }),
    },
  };

  const query = useQuery<GetCertificatesByBpnResponse, Error>({
    queryKey: ['search-certificates', params],
    queryFn: async (): Promise<GetCertificatesByBpnResponse> => {
      return await amplifyApiService.get<GetCertificatesByBpnResponse>(amplifyApiConfig);
    },
    ...options,
  });

  return query;
};

export const useWithdrawCertificatePermission = (
  options: UseMutationOptions<void, Error, WithdrawCertificatePermissionProps> = {},
): UseMutationResult<void, Error, WithdrawCertificatePermissionProps> => {
  const mutation = useMutation<void, Error, WithdrawCertificatePermissionProps>({
    mutationFn: async ({ permissionId }: WithdrawCertificatePermissionProps): Promise<void> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${CERTIFICATE_BASE_PATH}/${permissionId}/withdraw`,
        options: {},
      };

      return await amplifyApiService.delete<undefined, void>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const usePullCertificate = (
  options: UseMutationOptions<PullCertificateResponse, Error, PullCertificateProps> = {},
): UseMutationResult<PullCertificateResponse, Error, PullCertificateProps> => {
  const mutation = useMutation<PullCertificateResponse, Error, PullCertificateProps>({
    mutationFn: async ({
      bpn,
      id,
      url,
      offerId,
    }: PullCertificateProps): Promise<PullCertificateResponse> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${CERTIFICATE_BASE_PATH}/pull/${bpn}/${id}`,
        options: {
          body: { url, offerId },
        },
      };

      return await amplifyApiService.post<
        { url: string; offerId: string },
        PullCertificateResponse
      >(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useRequestCertificate = (
  options: UseMutationOptions<RequestCertificateResponse, Error, RequestCertificateProps> = {},
): UseMutationResult<RequestCertificateResponse, Error, RequestCertificateProps> => {
  const mutation = useMutation<RequestCertificateResponse, Error, RequestCertificateProps>({
    mutationFn: async ({
      type,
      bpn,
    }: RequestCertificateProps): Promise<RequestCertificateResponse> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${CERTIFICATE_BASE_PATH}/request`,
        options: {
          body: { type, bpn },
        },
      };

      return await amplifyApiService.post<
        { type: string; bpn: string },
        RequestCertificateResponse
      >(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};
